import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { changeTimezone, updateTenant } from "../../../services/user";
import {
  changeLanguage,
  useSelectLanguage,
} from "../../../redux/features/settings/settingsSlice";
import { setUser } from "../../../redux/features/user";
import { getAxiosResponseError } from "../../../utils/functions";
import { EMAIL_NOTIFICATIONS_QUERY_KEY } from "../emailNotifications/useEmailNotifications.logic";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Divider,
  Flex,
  LoadingOverlay,
  Select,
  Text,
} from "@mantine/core";
import IconClock from "@tabler/icons-react/dist/esm/icons/IconClock.mjs";
import IconDeviceFloppy from "@tabler/icons-react/dist/esm/icons/IconDeviceFloppy.mjs";
import IconWorld from "@tabler/icons-react/dist/esm/icons/IconWorld.mjs";
import { useTimezones } from "./useTimeZones";
import { useForm } from "react-hook-form";

const languageItems = [
  { label: "Български", value: "bg-BG" },
  { label: "English", value: "en-US" },
  { label: "Español", value: "es-ES" },
  { label: "Română", value: "ro-RO" },
  { label: "Русский", value: "ru-RU" },
];

const GeneralSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.user);
  const lg = useSelectLanguage();
  const { timezones, isLoading } = useTimezones();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    language: lg,
    timezone: user?.timezone || "",
  };

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { dirtyFields, isDirty },
    reset,
  } = useForm({
    defaultValues: initialValues,
  });

  const language = watch("language");
  const timezone = watch("timezone");

  async function onSubmit(data) {
    const payload = {};
    if (dirtyFields.language) payload.language = data.language;
    if (dirtyFields.timezone) payload.timezone = data.timezone;

    if (!Object.keys(payload).length) return;
    setLoading(true);

    if (payload.language) {
      await updateTenant({ language: payload.language });
      localStorage.setItem("tm-language", payload.language);
      dispatch(changeLanguage(payload.language));
      queryClient.invalidateQueries({
        queryKey: EMAIL_NOTIFICATIONS_QUERY_KEY,
      });
    }

    if (payload.timezone) {
      try {
        const user = await changeTimezone(payload.timezone);
        dispatch(setUser(user));
      } catch (err) {
        toast.error(getAxiosResponseError(err));
      }
    }
    setLoading(false);
    reset(data);
  }

  const LabelText = ({ children }) => <Text size="sm">{children}</Text>;
  const MainBox = ({ children }) => (
    <Box pos="relative">
      <Divider size="md" mb="sm" />
      {children}
      <Divider size="md" mt="sm" />
    </Box>
  );

  return (
    <MainBox className="general-settings-wrapper">
      <LoadingOverlay visible={isLoading || loading} overlayBlur={2} />
      <Flex justify="space-between" align="self-end" px="sm">
        <Flex gap={"md"}>
          <Flex gap={10} align="center">
            <LabelText>{t("settings.language")}</LabelText>
            <Select
              w={250}
              value={language}
              onChange={(val) =>
                setValue("language", val, { shouldDirty: true })
              }
              data={languageItems}
              leftSection={<IconWorld />}
              allowDeselect={false}
            />
          </Flex>

          <Flex gap={10} align="center">
            <LabelText>{t("settings.timezone")}</LabelText>
            <Select
              w={250}
              value={timezone}
              onChange={(val) =>
                setValue("timezone", val, { shouldDirty: true })
              }
              data={timezones}
              leftSection={<IconClock />}
              allowDeselect={false}
            />
          </Flex>
        </Flex>
        <Button
          leftSection={<IconDeviceFloppy />}
          onClick={handleSubmit(onSubmit)}
          size="sm"
          disabled={!isDirty}
        >
          SAVE
        </Button>
      </Flex>
    </MainBox>
  );
};

export default GeneralSettings;
